import Selector from '@components/inputs/selector/selector.component'
import Loader from '@components/loader/loader.component'
import CategoriesHelper from '@helpers/categories.helper'
import ObjectHelper from '@helpers/object.helper'
import Category from '@interfaces/category.interface'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './sub-categories-form-social-network.component.scoped.scss'
import './sub-categories-form-social-network.component.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  parentCategories?: Category[]
  externalCategories?: Category[]
  onCategoriesChange?: (categories: Category[]) => any
  maxCategories?: number
  minCategories?: number
  disabled?: boolean
}

export default ({
  className = '',
  maxCategories = 3,
  minCategories = 0,
  parentCategories = [],
  externalCategories = [],
  onCategoriesChange,
  disabled = false,
}: Props) => {
  const getOnlyWithParentCategories = useCallback(
    (cats: Category[]) => cats.filter((c) => parentCategories.find((p) => p.id === c?.parentId)),
    [parentCategories]
  )
  const [t, i18n] = useTranslation('subCategoriesFormSocialNetworkComponent')
  const [categories, setCategories] = useState<Category[]>([])
  const [selectedCategories, setSelectedCategories] = useState<Category[]>(getOnlyWithParentCategories(externalCategories))
  const objectHelper = useMemo(() => new ObjectHelper(), [])
  const categoriesHelper = useMemo(() => new CategoriesHelper(), [])
  const [loading, setLoading] = useState(true)
  const timestamp = `random-id-${(1 + Date.now() + Math.random()).toString().replace('.', '')}`

  const getCategoryByLanguage = useCallback(
    (category: Category): string => {
      if (i18n.language?.startsWith('pt')) {
        return category.description
      }
      if (i18n.language?.startsWith('es')) {
        return category.description_es
      }
      return category.description_en
    },
    [i18n.language]
  )

  const onSelect = useCallback(
    (checked: boolean, cat: Category) => {
      const newCategories = [...selectedCategories]

      if (checked) {
        newCategories.push(cat)
      }
      if (!checked) {
        newCategories.splice(
          newCategories?.findIndex((c) => c.id === cat?.id),
          1
        )
      }
      setSelectedCategories(newCategories)
      if (onCategoriesChange && typeof onCategoriesChange === 'function') {
        onCategoriesChange(newCategories)
      }
    },
    [onCategoriesChange, selectedCategories]
  )

  const isDisabled = useCallback(
    (category: Category) => {
      return (
        disabled ||
        !!(
          (selectedCategories.filter((c) => c.parentId === category.parentId)?.length >= maxCategories ||
            selectedCategories.filter((c) => c.parentId === category.parentId)?.length < minCategories) &&
          !selectedCategories.find((c) => c.id === category.id)
        )
      )
    },
    [disabled, selectedCategories, maxCategories, minCategories]
  )

  useEffect(() => {
    categoriesHelper.getCategories().then((response) => {
      setCategories(response)
      setLoading(false)
    })
  }, [categoriesHelper])

  useEffect(() => {
    if (!objectHelper?.compareObjects(externalCategories, selectedCategories)) {
      setSelectedCategories(getOnlyWithParentCategories(externalCategories))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalCategories])

  if (loading) {
    return (
      <div className="wrapper-loader text-center my-3">
        <Loader size="big" />
      </div>
    )
  }

  return (
    <div className={`form-sub-category ${className}`}>
      <legend>{t('label', { number: maxCategories })}</legend>
      <div className="wrapper-selectors-all">
        <div className="row">
          {categories
            ?.filter((c) => parentCategories?.find((p) => p.id === c.id))
            ?.sort((a: Category, b: Category) => (getCategoryByLanguage(a) > getCategoryByLanguage(b) ? 1 : -1))
            ?.map((category) => {
              return (
                <div className="col-24 col-lg-8" key={category?.id}>
                  <Selector
                    name={`select-category-parent-${timestamp}`}
                    id={`select-category-parent-${category?.id}-${timestamp}`}
                    errorSpan={false}
                    className="selector-category-parent"
                    readOnly
                    value={category?.id}
                    checked={true}
                    leftLabel={
                      <span className="parent-category-label readonly-category">
                        <span className="emoji">{category?.emoji}</span>
                        <span className="text">{getCategoryByLanguage(category)}</span>
                      </span>
                    }
                  />
                  {!category?.children?.length ? (
                    <div className="sub-category custom-disabled">
                      <Selector
                        name={`select-sub-category-empty-${timestamp}`}
                        id={`select-sub-category-empty-${timestamp}`}
                        errorSpan={false}
                        className="selector-sub-category"
                        disabled
                        leftLabel={
                          <span className={'sub-category-label disabled-span'}>
                            <span className="emoji"></span>
                            <span className="text">{t('empty')}</span>
                          </span>
                        }
                      />
                    </div>
                  ) : (
                    <div className="sub-category">
                      {category?.children?.map((subcategory: Category) => {
                        return (
                          <Selector
                            key={subcategory?.id}
                            name={`select-sub-category-${category?.id}-${timestamp}`}
                            id={`select-sub-category-${category?.id}-${subcategory?.id}-${timestamp}`}
                            errorSpan={false}
                            className="selector-sub-category"
                            disabled={isDisabled(subcategory)}
                            value={subcategory?.id}
                            checked={!!selectedCategories?.find((c) => c.id === subcategory?.id)}
                            leftLabel={
                              <span className={`sub-category-label ${isDisabled(subcategory) ? 'disabled-span' : ''}`}>
                                <span className="emoji">{subcategory?.emoji}</span>
                                <span className="text">{getCategoryByLanguage(subcategory)}</span>
                              </span>
                            }
                            onChange={(checked: boolean) => onSelect(checked, subcategory)}
                          />
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}
